import { useState } from "react";
import { AreaChart, CartesianGrid, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getCssValue } from "../../charts/utils";
import { getEmissionsLineThemeColors, getTooltipCursorStyle } from "../../colors";
import {
    ChartArea,
    ChartDataTable,
    CustomLegend,
    EmissionsChartTooltip,
    LegendChartsIcon,
    LegendDashIcon,
    LegendLineIcon,
    XAxisTick,
    YAxisTick,
} from "../../components";
import { buildXAxisYears, shouldShowXAxisYear, TOOLTIP_OFFSET } from "../chart-constants";
import type { AreaData, AreaNavItem, LevelConfig } from "./data-config";
import {
    EMISSIONS_LINE_LABELS,
    EMISSIONS_UNIT,
    formatNumberToTwoDecimals,
    getYAxisDomain,
    getYAxisTicks,
} from "./emissions-chart-utils";

export interface ChartDataWithEffect extends Omit<AreaData, "utenEnova"> {
    utenEnova: number | null;
    totalAreas: number | null;
    effectDifference: number;
}

interface EmissionsChartProps {
    chartData: AreaData[];
    chartDataWithEffect: ChartDataWithEffect[];
    level: number;
    legendHeading: string;
    effectPatternDotColor: string;
    currentLevelConfig: LevelConfig;
    navItems: AreaNavItem[];
    normalizedAccumulatedDifferenceValue: number;
    hoveredNavKey: string | null;
    onAreaHover?: (navKey: string | null) => void;
}

const getAreaVisuals = (navItem: AreaNavItem, hoveredNavKey: string | null) => {
    const isHovered = hoveredNavKey === navItem.key;
    const baseFill = navItem.fill;
    const hoverFill = navItem.hoverFill ?? navItem.fill;
    const baseStroke = navItem.stroke ?? navItem.fill;
    const hoverStroke = navItem.hoverStroke ?? navItem.hoverFill ?? baseStroke;
    return {
        fill: isHovered ? hoverFill : baseFill,
        stroke: isHovered ? hoverStroke : baseStroke,
        fillOpacity: 1,
    };
};

const EmissionsChart: React.FC<EmissionsChartProps> = ({
    chartData,
    chartDataWithEffect,
    level,
    legendHeading,
    effectPatternDotColor,
    currentLevelConfig,
    navItems,
    normalizedAccumulatedDifferenceValue,
    hoveredNavKey,
    onAreaHover,
}) => {
    const colors = getEmissionsLineThemeColors();
    const tooltipCursor = getTooltipCursorStyle();

    // Build the allowed year set used to show/hide tooltip content and the vertical cursor line: all years in yearly mode, or only 2022 + 5-year ticks in 5-year mode.
    const [activeYear, setActiveYear] = useState<number | null>(null);
    const xAxisTicks = buildXAxisYears(chartDataWithEffect.map((entry) => entry.year));
    const tooltipVisibleYears = new Set(xAxisTicks);
    const isCursorVisible = activeYear === null || shouldShowXAxisYear(activeYear);

    const handleAreaHover = (navKey: string | null) => {
        if (onAreaHover) {
            onAreaHover(navKey);
        }
    };

    const visibleAreaNavItems = navItems
        .map((navItem) => ({
            navItem,
            hasData: chartDataWithEffect.some((d) => d[navItem.area] > 0),
        }))
        .reverse()
        .filter(({ hasData }) => hasData)
        .map(({ navItem }) => navItem);

    const dataTableColumns = [
        { key: "year", label: "År", getValue: (row: ChartDataWithEffect) => String(row.year) },
        ...visibleAreaNavItems.map((navItem) => ({
            key: navItem.area,
            label: navItem.label,
            getValue: (row: ChartDataWithEffect) => formatNumberToTwoDecimals(row[navItem.area]),
        })),
        {
            key: "utenEnova",
            label: EMISSIONS_LINE_LABELS.utenEnova,
            getValue: (row: ChartDataWithEffect) => formatNumberToTwoDecimals(row.utenEnova ?? 0),
        },
        ...(level === 1
            ? [
                  {
                      key: "klimamal",
                      label: EMISSIONS_LINE_LABELS.klimamal,
                      getValue: (row: ChartDataWithEffect) => formatNumberToTwoDecimals(row.klimamal),
                  },
              ]
            : []),
        {
            key: "effectDifference",
            label: "Effekten av Enova",
            getValue: (row: ChartDataWithEffect) => formatNumberToTwoDecimals(row.effectDifference),
        },
    ];

    return (
        <>
            <ChartDataTable
                caption="Tallgrunnlag for utslippsutvikling per år"
                columns={dataTableColumns}
                rowKey={(row) => String(row.year)}
                rows={chartDataWithEffect}
            />
            <ResponsiveContainer width="100%" height={600}>
                <AreaChart
                    data={chartDataWithEffect}
                    key={JSON.stringify(chartDataWithEffect)}
                    margin={{ top: 0, right: 30, left: 0, bottom: 20 }}
                    onMouseMove={(state) => {
                        const year = Number(state?.activeLabel);
                        setActiveYear(Number.isFinite(year) ? year : null);
                    }}
                    onMouseLeave={() => setActiveYear(null)}
                >
                    <defs>
                        <pattern id="effectPattern" patternUnits="userSpaceOnUse" width={8} height={8}>
                            <rect width={8} height={8} fill="var(--effect-card-background)" />
                            <circle cx={2} cy={2} r={1.25} fill={effectPatternDotColor} />
                            <circle cx={6} cy={6} r={1.25} fill={effectPatternDotColor} />
                        </pattern>
                    </defs>

                    <CartesianGrid strokeDasharray="1 1" vertical={false} syncWithTicks />

                    <XAxis
                        dataKey="year"
                        tick={<XAxisTick />}
                        interval={0}
                        ticks={xAxisTicks}
                        label={{
                            value: "År",
                            position: { x: 850, y: 4 },
                            fill: "var(--ds-color-neutral-text-default)",
                            fontSize: "var(--ds-font-size-1)",
                            fontWeight: 600,
                        }}
                    />

                    <YAxis
                        tick={<YAxisTick />}
                        domain={getYAxisDomain(chartData, level)}
                        ticks={getYAxisTicks(chartData, level)}
                        interval={0}
                        label={{
                            value: EMISSIONS_UNIT,
                            position: { x: 84, y: -10 },
                            fill: "var(--ds-color-neutral-text-default)",
                            fontSize: "var(--ds-font-size-1)",
                            fontWeight: 600,
                        }}
                        padding={{ top: 20, bottom: 0 }}
                    />

                    <Tooltip
                        content={(tooltipProps) => {
                            const year = Number(tooltipProps.label);
                            if (Number.isFinite(year) && !tooltipVisibleYears.has(year)) {
                                return null;
                            }

                            return (
                                <EmissionsChartTooltip
                                    active={tooltipProps.active}
                                    payload={tooltipProps.payload}
                                    levelConfig={{ ...currentLevelConfig, navItems }}
                                />
                            );
                        }}
                        offset={TOOLTIP_OFFSET}
                        cursor={isCursorVisible ? tooltipCursor : false}
                    />

                    <Legend
                        verticalAlign="top"
                        wrapperStyle={{ paddingBottom: "40px" }}
                        content={
                            <CustomLegend heading={legendHeading}>
                                {normalizedAccumulatedDifferenceValue > 0 ? (
                                    <>
                                        <LegendChartsIcon
                                            label={EMISSIONS_LINE_LABELS.medEnova}
                                            tooltip="Utslippsutviklingen vi tror på med dagens politikk og Enovas virkemidler."
                                        />
                                        <LegendLineIcon
                                            label={EMISSIONS_LINE_LABELS.utenEnova}
                                            stroke={colors.withoutEnova}
                                            tooltip="Hvordan utslippene ville utviklet seg hvis Enova ikke fantes."
                                        />
                                        {level === 1 && (
                                            <LegendDashIcon
                                                label={EMISSIONS_LINE_LABELS.klimamal}
                                                tooltip="Utvikling der utslippene er 55, 75 og 95 % lavere i 2030, 2035 og 2050 enn i 1990."
                                                stroke={colors.klimaMal}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <LegendLineIcon
                                            label={EMISSIONS_LINE_LABELS.forventetUtvikling}
                                            stroke={colors.withoutEnova}
                                            tooltip="Utslippsutviklingen vi tror på med dagens politikk."
                                        />
                                        {level === 1 && (
                                            <LegendDashIcon
                                                label={EMISSIONS_LINE_LABELS.klimamal}
                                                tooltip="Utvikling der utslippene er 55, 75 og 95 % lavere i 2030, 2035 og 2050 enn i 1990."
                                                stroke={colors.klimaMal}
                                            />
                                        )}
                                    </>
                                )}
                            </CustomLegend>
                        }
                    />

                    {visibleAreaNavItems.map((navItem) => {
                        const areaKey = navItem.area as keyof ChartDataWithEffect;
                        const areaVisuals = getAreaVisuals(navItem, hoveredNavKey);
                        const hoverHandlers =
                            level < 4
                                ? {
                                      onMouseEnter: () => handleAreaHover(navItem.key),
                                      onMouseLeave: () => handleAreaHover(null),
                                  }
                                : {};

                        return (
                            <ChartArea
                                activeDot={true} // show active dot for every year, even though cursor line and tooltip might only show every 5 years (see SHOW_X_AXIS_EVERY_5_YEARS)
                                dataKey={areaKey}
                                fillColor={areaVisuals.fill}
                                fillOpacity={areaVisuals.fillOpacity}
                                key={areaKey}
                                name={navItem.label}
                                strokeColor={areaVisuals.stroke}
                                zIndex={10}
                                {...hoverHandlers}
                            />
                        );
                    })}

                    <Line
                        activeDot={{
                            fill: colors.withoutEnova,
                        }} // show active dot for every year, even though cursor line and tooltip might only show every 5 years (see SHOW_X_AXIS_EVERY_5_YEARS)
                        type="linear"
                        dataKey="utenEnova"
                        name={EMISSIONS_LINE_LABELS.utenEnova}
                        stroke={colors.withoutEnova}
                        strokeWidth={2}
                        dot={false}
                        zIndex={10}
                    />

                    {level === 1 && (
                        <Line
                            activeDot={true}
                            type="linear"
                            dataKey="klimamal"
                            name={EMISSIONS_LINE_LABELS.klimamal}
                            stroke={colors.klimaMal}
                            strokeWidth={2}
                            dot={false}
                            strokeDasharray="5 5"
                            zIndex={100}
                        />
                    )}

                    <ChartArea
                        activeDot={{
                            fill: getCssValue("--ds-color-fjellgronn-visualisation-16", "#102625"),
                        }}
                        dataKey="effectDifference"
                        dot={false}
                        fillColor="url(#effectPattern)"
                        fillOpacity={1}
                        isAnimationActive={true}
                        name="Effekten av Enova"
                        zIndex={1}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </>
    );
};

export default EmissionsChart;
